import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { BalanceWithDeltaCell, InflationTooltipContent } from '../cells';
import { currency } from '../formatters';
import { PROJECTED_COST_NOTE } from '../columnGroups';
import { GrowthFactor, HeaderTooltip } from '../HeaderTooltip';
import type { ColumnGroup } from '../types';
import type { StrategyPresentation } from './types';

type CmTwoLikeDeltas = {
  baseBenefitPaid?: number;
  riderBenefitPaid?: number;
  epLumpSumPaid?: number;
  epLumpSumBasePaid?: number;
  epLumpSumDays?: number;
  epLumpSumDailyRate?: number;
  indemnitySurplusAccrued?: number;
  indemnitySurplusApplied?: number;
};
type CmTwoLikeState = {
  indemnitySurplus?: number;
};

const surplusOf = (s: MonthlyState): number =>
  (s.strategyState as CmTwoLikeState).indemnitySurplus ?? 0;

function surplusTooltipFor(month: MonthlyEntry, openState: MonthlyState) {
  const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
  const accrued = sd.indemnitySurplusAccrued ?? 0;
  const applied = sd.indemnitySurplusApplied ?? 0;
  const open = surplusOf(openState);
  const close = surplusOf(month.state);
  if (open === 0 && close === 0 && accrued === 0 && applied === 0) return null;
  return (
    <BalanceBreakdownTooltip
      title="Indemnity surplus"
      description="Indemnity policies pay a fixed monthly benefit regardless of actual cost. Months where the benefit exceeds cost accrue the excess as a surplus reserve, which is drawn down later to cover months where cost exceeds the benefit."
      sections={[
        {
          opening: open,
          closing: close,
          lines: [
            { label: 'Accrued (benefit > cost)', value: accrued, kind: 'add' },
            { label: 'Applied (cost > benefit)', value: applied, kind: 'subtract' },
          ],
        },
      ]}
    />
  );
}

function surplusColumnGroup(): ColumnGroup {
  return {
    title: 'Running balances',
    columns: [
      {
        key: 'surplus',
        label: 'Surplus balance',
        tooltip:
          'End-of-month surplus balance, with this month’s change beneath (+ accrued / − applied).',
        render: ({ month, openState }) => (
          <BalanceWithDeltaCell
            opening={surplusOf(openState)}
            closing={surplusOf(month.state)}
            tooltipContent={surplusTooltipFor(month, openState) ?? undefined}
          />
        ),
        renderAggregate: ({ openStateAtStart, closeStateAtEnd, kind }) => (
          <BalanceWithDeltaCell
            opening={kind === 'total' ? surplusOf(closeStateAtEnd) : surplusOf(openStateAtStart)}
            closing={surplusOf(closeStateAtEnd)}
          />
        ),
      },
    ],
  };
}

function EpLumpTooltipContent({
  days,
  dailyRate,
  total,
}: {
  days: number;
  dailyRate: number;
  total: number;
}) {
  return (
    <TooltipContent className="text-xs">
      <div className="mb-1 font-semibold">Waiting period reimbursement</div>
      <div className="mb-1 text-[10px] text-gray-300">
        Once the waiting period is satisfied, days served during it are paid
        back as a lump sum at the inflated daily benefit on the satisfaction
        date.
      </div>
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Days served</span>
        <span className="font-semibold tabular-nums">{days}</span>
        <span className="text-gray-300">Inflated daily rate</span>
        <span className="font-semibold tabular-nums">{currency(dailyRate)}/day</span>
        <span className="text-gray-300">Lump sum</span>
        <span className="font-semibold tabular-nums">
          {days} × {currency(dailyRate)} = {currency(total)}
        </span>
      </div>
    </TooltipContent>
  );
}

export const cmTwoPresentation: StrategyPresentation = {
  benefitAmountCell: ({ month, terms }) => {
    // Pre-policy rows carry no applied cap; fall through to the default cell
    // (which renders a dash when there's no snapshot for the year).
    if (month.events.ineligibilityReason === 'pre-policy') return null;
    const baseDaily =
      terms.nominalDailyBenefitByEnvironment?.[month.context.careEnvironment] ??
      terms.nominalDailyBenefitBase;
    const baseMonthly = baseDaily * DAYS_IN_MONTH;
    const isSimple = terms.inflationIsSimple === true;
    // Headline = the engine's own applied base + rider ceiling for this row
    // (computed on the engine's rider clock, which pauses on paid months
    // during the waiting period). Base + rider is algebraically baseMonthly ×
    // growth factor, so it maps onto the shared inflation tooltip (compound:
    // ×(1+r)^n; simple: ×(1 + r·n)).
    const monthlyCap = month.facts.appliedMonthlyBenefitCap;

    const intYear = Math.floor(month.context.policyYear);
    const cap = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
    // Increment count = policyYear − 1, capped at capYears, matching the
    // engine's rider escalation so the formula text reconciles with the
    // displayed result (it can differ slightly on rows where the engine's
    // paid-month rider clock has paused behind the calendar clock).
    const rawIncrements = Math.max(intYear - 1, 0);
    const increments = cap !== null ? Math.min(rawIncrements, cap) : rawIncrements;
    const capped = cap !== null && intYear > cap;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="leading-tight cursor-help">
            <div className="font-medium text-gray-900">{currency(monthlyCap)}</div>
            <div className="text-[10px] text-gray-500">
              {currency(monthlyCap / DAYS_IN_MONTH)}/day
            </div>
          </div>
        </TooltipTrigger>
        <InflationTooltipContent
          header={`Policy year ${intYear}`}
          startingLabel="At issue"
          startingValue={baseMonthly}
          rate={terms.inflationRate}
          years={increments}
          result={monthlyCap}
          note={capped ? `Rider capped at year ${cap}` : undefined}
          compoundLabel={isSimple ? 'Simple growth' : 'Compound rider growth'}
          isSimple={isSimple}
        />
      </Tooltip>
    );
  },

  benefitAmountValueForMonth: ({ month }) =>
    month.events.ineligibilityReason === 'pre-policy'
      ? undefined
      : month.facts.appliedMonthlyBenefitCap,

  // Surplus only makes sense on indemnity products — reimbursement products
  // can't accrue a surplus reserve. Gated here so a future reimbursement
  // hybrid routed through this presentation would correctly suppress it.
  extraPayoutColumnGroups: ({ terms }) =>
    terms.isIndemnity ? [surplusColumnGroup()] : [],

  poolCellTooltip: ({ month, openState }) => {
    const open = openState.pools.baseBenefitPool;
    const close = month.state.pools.baseBenefitPool;
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const baseDrawn = sd.baseBenefitPaid ?? 0;
    const lumpBase = sd.epLumpSumBasePaid ?? 0;
    if (open === 0 && close === 0 && baseDrawn === 0 && lumpBase === 0) return null;
    return (
      <BalanceBreakdownTooltip
        title="LTC pool"
        description="CareMatters II's base pool. Only the base monthly benefit (and the base portion of any EP retroactive lump) draws down the pool — the inflation rider is paid on top and does not deplete it."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              { label: 'Base benefit drawn', value: baseDrawn, kind: 'subtract' },
              { label: 'EP retroactive (base portion)', value: lumpBase, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  deathBenefitCellTooltip: ({ month, openState }) => {
    const open = openState.pools.deathBenefit;
    const close = month.state.pools.deathBenefit;
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const baseDrawn = sd.baseBenefitPaid ?? 0;
    const lumpBase = sd.epLumpSumBasePaid ?? 0;
    if (open === 0 && close === 0 && baseDrawn === 0 && lumpBase === 0) return null;
    return (
      <BalanceBreakdownTooltip
        title="Death benefit"
        description="Flat Face Amount, depleted by the base benefit payout (and the base portion of any EP retroactive lump). The inflation rider is paid on top and does not deplete the death benefit. Floors at the minimum guaranteed amount."
        sections={[
          {
            opening: open,
            closing: close,
            lines: [
              { label: 'Base benefit drawn', value: baseDrawn, kind: 'subtract' },
              { label: 'EP retroactive (base portion)', value: lumpBase, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  payoutCellTooltip: ({ month }) => {
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const basePay = sd.baseBenefitPaid ?? 0;
    const riderPay = sd.riderBenefitPaid ?? 0;
    const total = basePay + riderPay;
    const cost = month.facts.effectiveCareCost;
    const indemnityPayout = month.deltas.payout.indemnityPayout;
    const surplusApplied = sd.indemnitySurplusApplied ?? 0;
    const surplusAccrued = sd.indemnitySurplusAccrued ?? 0;
    if (total === 0 && (sd.epLumpSumPaid ?? 0) === 0) return null;

    const rule =
      indemnityPayout >= cost
        ? 'CareMatters II pays a fixed monthly indemnity benefit regardless of actual cost. This month\'s benefit covers the full cost; the excess accrues as a surplus reserve.'
        : 'CareMatters II pays a fixed monthly indemnity benefit. The benefit is below this month\'s cost — the surplus reserve (if any) covers part of the gap; the remainder is out-of-pocket.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — indemnity</div>
        <div className="mb-1 text-[10px] text-gray-300">{rule}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          <span className="text-gray-300">Eligible care cost</span>
          <span className="font-semibold tabular-nums">{currency(cost)}</span>
          <span className="text-gray-300">Base benefit</span>
          <span className="font-semibold tabular-nums">{currency(basePay)}</span>
          {riderPay > 0 && (
            <>
              <span className="text-gray-300">+ Inflation rider</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(riderPay)}
              </span>
            </>
          )}
          <span className="text-gray-300">Total paid</span>
          <span className="font-semibold tabular-nums">{currency(total)}</span>
          {surplusAccrued > 0 && (
            <>
              <span className="text-gray-300">Surplus accrued</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(surplusAccrued)}
              </span>
            </>
          )}
          {surplusApplied > 0 && (
            <>
              <span className="text-gray-300">Surplus applied</span>
              <span className="font-semibold tabular-nums text-emerald-300">
                +{currency(surplusApplied)}
              </span>
            </>
          )}
        </div>
      </TooltipContent>
    );
  },

  paidCellExtras: month => {
    const sd = month.deltas.strategyDeltas as CmTwoLikeDeltas;
    const surplusApplied = sd.indemnitySurplusApplied ?? 0;
    const epLump = sd.epLumpSumPaid ?? 0;
    const covered = month.deltas.payout.ltcCoveragePaid;
    const withSurplus = month.deltas.payout.ltcPayoutToCost;

    if (surplusApplied === 0 && epLump === 0) return null;

    return (
      <>
        {surplusApplied > 0 && withSurplus !== covered && (
          <div className="text-[10px] text-emerald-700 inline-block">
            +{currency(surplusApplied)} surplus
          </div>
        )}
        {epLump > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-[10px] text-amber-700 cursor-help border-b border-dotted border-amber-300 inline-block">
                +{currency(epLump)} WP reimbursement
              </div>
            </TooltipTrigger>
            <EpLumpTooltipContent
              days={sd.epLumpSumDays ?? 0}
              dailyRate={sd.epLumpSumDailyRate ?? 0}
              total={epLump}
            />
          </Tooltip>
        )}
      </>
    );
  },

  // CM II's base pool is flat — it does not inflate at policy anniversaries.
  // The rider grows the monthly benefit instead. A "Pool limit" column would
  // just echo the static base pool every year, so we hide it.
  hiddenColumns: ['pool-limit'],

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC base-pool balance, with this month's change beneath. The base pool is flat (no anniversary inflation) — the inflation rider grows the monthly benefit instead."
        formulas={[
          { label: 'Closing pool', expression: 'opening − base benefit drawn this month' },
          { label: 'Rider', expression: 'paid on top of base; does not deplete the pool' },
        ]}
        notes="The base portion of any waiting-period retroactive lump also draws the pool."
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Indemnity payout this month, with cumulative dollars paid by the policy through this month in parentheses. CareMatters II pays a fixed monthly indemnity benefit regardless of actual cost."
        formulas={[
          { label: 'Monthly payout', expression: 'base benefit + inflation rider (+ retroactive WP lump, if any)' },
          { label: 'Cumulative', expression: 'Σ monthly payouts through this month' },
        ]}
        notes="When the benefit exceeds cost, the surplus accrues to the indemnity reserve; when cost exceeds the benefit, the reserve is drawn down to close the gap."
      />
    ),
    costTooltip: (
      <HeaderTooltip
        description="Projected total monthly care cost (with inflated per-day rate beneath). The daily rate comes from the care plan's phase × environment unit cost."
        formulas={[
          {
            label: 'Daily cost',
            expression: (
              <>
                today's daily unit cost × <GrowthFactor rate="care-cost inflation" exponent="years from today" />
              </>
            ),
          },
          { label: 'Monthly cost', expression: 'daily cost × days in month' },
        ]}
        notes={PROJECTED_COST_NOTE}
      />
    ),
    benefitAmountTooltip: (
      <HeaderTooltip
        description="Policy monthly benefit maximum for this policy year (base + inflation rider), with the daily equivalent beneath."
        formulas={[
          { label: 'Base monthly', expression: 'at-issue daily benefit × days in month' },
          {
            label: 'With rider',
            expression: (
              <>
                base monthly × <GrowthFactor rate="rider rate" exponent="policy year − 1" />
              </>
            ),
          },
        ]}
        notes="Rider growth is compound by default and capped at the rider's cap year if specified; simple-interest variants use (1 + rate × years) instead."
      />
    ),
    drawnTooltip: (
      <HeaderTooltip
        description="Cumulative reduction of the base pool through this month. The inflation rider is paid on top and does not draw down the pool."
        formulas={[
          { label: 'Drawn', expression: 'Σ (base benefit drawn each month)' },
          { label: 'Identity', expression: 'Base pool − Drawn = Base pool remaining' },
        ]}
        notes="The base portion of any WP retroactive lump is included in this total."
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. CareMatters II has a flat Face Amount that is depleted dollar-for-dollar by the base benefit (not the rider)."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue Face Amount − Σ (base benefit drawn)' },
          { label: 'Floor', expression: 'max(above, minimum guaranteed amount)' },
        ]}
      />
    ),
    benefitInflationCompoundLabel: 'Compound rider growth',
    benefitInflationCapNote: capYear => `Rider capped at year ${capYear}`,
  },
};
